"use client";
import {
	AnimatePresence,
	motion,
	useScroll,
	useTransform,
} from "framer-motion";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { AdaptiveText, type AdaptiveTextProps, Title } from "./Content";
import ItemIcon, { type ItemIconProps } from "./ItemIcon";

// Define a type for our items
export type ScrollItem = {
	id: number;
	title: string;
	description: AdaptiveTextProps["portableText"];
	image: string;
	icon?: ItemIconProps["icon"];
	iconUrl?: string;
	alt?: string;
	isImage?: boolean;
};

type Props = {
	items: ScrollItem[];
};

export const StickyScrollContainer = ({ items }: Props) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const [isScrollingUp, setIsScrollingUp] = useState(false);
	const [_isVisible, setIsVisible] = useState(true);
	const lastScrollY = useRef(0);

	// Create a scroll progress for the entire container section
	const { scrollYProgress } = useScroll({
		target: containerRef,
		offset: ["start start", "end end"],
	});

	// Enhanced opacity transitions with entrance/exit thresholds
	const imageOpacity = useTransform(
		scrollYProgress,
		[0, 0.05, 0.3, 0.7, 0.95, 1],
		[1, 1, 1, 1, 1, 1],
	);

	// Progress indicator
	const progress = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

	// Detect scroll direction
	useEffect(() => {
		const handleScroll = () => {
			const currentScrollY = window.scrollY;
			setIsScrollingUp(currentScrollY < lastScrollY.current);
			lastScrollY.current = currentScrollY;

			// Set visibility based on scroll position
			if (containerRef.current) {
				const rect = containerRef.current.getBoundingClientRect();
				setIsVisible(rect.top < window.innerHeight && rect.bottom > 0);
			}
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	// Update active index based on scroll progress with direction awareness
	useEffect(() => {
		const unsubscribe = scrollYProgress.on("change", (progress) => {
			let newIndex: number;

			if (progress <= 0.05) {
				newIndex = 0;
			} else if (progress >= 0.95) {
				newIndex = items.length - 1;
			} else {
				const adjustedProgress = (progress - 0.05) / 0.9;
				const rawIndex = adjustedProgress * items.length;

				if (isScrollingUp) {
					newIndex = Math.ceil(rawIndex - 0.3);
				} else {
					newIndex = Math.floor(rawIndex);
				}

				newIndex = Math.max(0, Math.min(newIndex, items.length - 1));
			}

			if (newIndex !== activeIndex) {
				setActiveIndex(newIndex);
			}
		});

		return () => unsubscribe();
	}, [activeIndex, items.length, scrollYProgress, isScrollingUp]);

	return (
		<div
			ref={containerRef}
			className="relative w-full"
			style={{
				height: `${items.length * 60}vh`,
			}}
		>
			{/* Sticky container */}
			<div className="sticky top-24 flex h-screen w-full flex-col">
				{/* Progress bar */}
				<motion.div
					className="absolute top-0 left-0 z-20 h-1.5 bg-primary"
					style={{ width: progress }}
				/>

				{/* Text Section - Single item with fade transition */}
				<div className="relative flex h-[25%] w-full items-start justify-center bg-gradient-to-b from-transparent to-background p-4 pt-4 pb-0 backdrop-blur-sm sm:h-[15%]">
					<AnimatePresence mode="wait">
						{items[activeIndex] && (
							<motion.div
								key={`text-${items[activeIndex].id}`}
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -20 }}
								transition={{ duration: 0.3, ease: "easeOut" }}
								className="w-full text-left"
							>
								<div className="relative mt-8 flex flex-col pt-16 md:pt-0 md:pl-16">
									<ItemIcon
										icon={items[activeIndex].icon}
										iconUrl={items[activeIndex].iconUrl}
										alt={items[activeIndex].alt}
										isImage={items[activeIndex].isImage}
										className="-top-4 absolute left-0 flex size-12 items-center justify-center rounded-xl border border-gray-100 bg-white p-2 text-primary shadow-lg ring-1 ring-gray-200 md:top-0"
									/>
									<Title
										as="h3"
										className="mt-0 max-w-3xl text-primary text-xl sm:text-2xl/[0.9] md:text-3xl/[0.9]"
									>
										{items[activeIndex].title}
									</Title>

									<AdaptiveText
										portableText={items[activeIndex].description}
										className="max-w-3xl text-base sm:text-lg"
									/>
								</div>
							</motion.div>
						)}
					</AnimatePresence>
				</div>

				{/* Image Section */}
				<motion.div
					style={{ opacity: imageOpacity }}
					className="mt-0 flex w-full items-center justify-center p-3"
				>
					<AnimatePresence mode="wait">
						{items[activeIndex] && (
							<motion.div
								key={`image-${items[activeIndex].id}`}
								initial={{ opacity: 0, x: 50 }}
								animate={{ opacity: 1, x: 0 }}
								exit={{ opacity: 0, x: -50 }}
								transition={{
									duration: 0.3,
									ease: "easeOut",
								}}
								className="relative mt-32 aspect-[3/4] max-h-[40vh] w-full overflow-hidden rounded-lg border border-gray-100 bg-white shadow-lg sm:max-h-[60vh] md:mt-16"
							>
								<Image
									src={items[activeIndex].image || "/placeholder.svg"}
									alt={items[activeIndex].title}
									fill
									sizes="(max-width: 768px) 100vw, (max-width: 1200px) 70vw, 1200px"
									quality={100}
									className="h-full w-full object-contain p-3"
								/>
							</motion.div>
						)}
					</AnimatePresence>
				</motion.div>
			</div>

			{/* Extra padding div to ensure last item is fully visible on scroll */}
			<div className="h-24" />
		</div>
	);
};
